// The strip of jobs ON HOLD on the Scheduling page (Session SC9A, Director 22 October 2026). It is NOT a page and NOT a menu item: one capped
// strip at the top of the page the person is already on, derived live from the job status (parts_pending), hidden when nothing is held. Each
// row names the job, the site, the engineer and the reason ("On hold: awaiting parts"), and opens the job. "See all" opens the All jobs tab
// filtered to the held jobs. It reads only: putting a job on hold or resuming it stays in the visit's popover.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type { JobListItem } from "../../lib/api";
import { StatusPill } from "../../components/StatusPill";
import { primaryStatePill } from "../../lib/statusRegistry";
import { jobTypeLabel } from "../../lib/jobType";
import { formatDate } from "../../lib/format";
import { jobRecordHref } from "./JobsListPage";

/** At most this many held jobs are listed; the rest are one link away. */
export const HELD_STRIP_LIMIT = 5;

export function HeldJobsStrip({ reloadKey = 0 }: { reloadKey?: number }) {
  const [rows, setRows] = useState<JobListItem[]>([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let live = true;
    // Promise.resolve().then so a failure to even start the read is the same quiet no-strip as a failed read.
    Promise.resolve()
      .then(() => api.scheduling.jobList({ status: ["on_hold"], sort: "date", dir: "asc", limit: HELD_STRIP_LIMIT, offset: 0 }))
      .then((r) => {
        if (!live) return;
        setRows(r.items);
        setTotal(r.total);
      })
      // A failed read shows nothing rather than a false "none on hold": the strip is a convenience, the All jobs filter is the record.
      .catch(() => {
        if (!live) return;
        setRows([]);
        setTotal(0);
      });
    return () => {
      live = false;
    };
  }, [reloadKey]);

  if (total === 0 || rows.length === 0) return null;

  return (
    <section data-testid="held-jobs" aria-label="Jobs on hold" className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-base text-amber-400">On hold ({total})</h2>
        <Link to="/office/jobs?status=on_hold" className="text-xs text-ele-green underline-offset-2 hover:underline">
          {total > rows.length ? `See all ${total}` : "Open in All jobs"}
        </Link>
      </div>
      <ul className="space-y-1.5">
        {rows.map((j) => {
          const pill = primaryStatePill({ status: j.status, holdReason: j.holdReason, holdReasonText: j.holdReasonText });
          return (
            <li key={j.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              {pill ? <StatusPill state={pill} /> : <StatusPill domain="job" status={j.status} />}
              <Link to={jobRecordHref(j)} className="font-heading text-ele-strong hover:text-ele-green hover:underline">
                {j.reference ?? j.eleNumber ?? jobTypeLabel(j.type)}
              </Link>
              <span className="text-ele-body">{[j.siteName, j.engineerName ?? "No engineer", j.scheduledFor ? formatDate(j.scheduledFor) : "No date"].filter(Boolean).join(" | ")}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
